import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import logoAnimation from './assets/bbyb-logo-lottie.json';
import Login from './components/Login';
import MainLayout from './components/MainLayout';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import IntroPage from './pages/IntroPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ToastContainer from './components/ToastContainer';
import { api, setAccessToken, clearAccessToken, getAccessToken } from './services/api';

const TOAST_DURATION = 4500;

const App = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [simplified, setSimplified] = useState(
        () => localStorage.getItem('storageLabels') === 'simplified'
    );

    // ── Bell notifications (DB-backed, persistent, user-specific) ──────────
    const [notifications, setNotifications] = useState([]);

    const fetchNotifications = useCallback(async () => {
        try {
            const data = await api.getNotifications();
            setNotifications(data.notifications || []);
        } catch (e) {
            console.error('Failed to fetch notifications', e);
        }
    }, []);

    const markAllRead = useCallback(async () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        try { await api.markAllNotificationsRead(); } catch (e) { console.error(e); }
    }, []);

    // "Clear all" from bell panel — explicit user action, deletes from DB
    const clearNotifications = useCallback(async () => {
        setNotifications([]);
        try { await api.clearNotifications(); } catch (e) { console.error(e); }
    }, []);

    // ── Ephemeral toasts (action feedback, never stored in DB) ─────────────
    const [toasts, setToasts] = useState([]);

    const addNotification = useCallback((notif) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        setToasts(prev => [...prev, { id, ...notif }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, TOAST_DURATION);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // ── Auth ───────────────────────────────────────────────────────────────
    const fetchProfile = useCallback(async () => {
        try {
            const u = await api.getProfile();
            setUser(u);
            setIsAuthenticated(true);
        } catch {
            handleLogout();
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // On mount: try to silently refresh using the httpOnly cookie.
    // If it succeeds, the user is still logged in.
    useEffect(() => {
        const tryRestore = async () => {
            const token = await api.refresh();
            if (token) {
                await fetchProfile();
                fetchNotifications();
            } else {
                setLoading(false);
            }
        };
        tryRestore();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll notifications while authenticated (pause when tab is hidden)
    useEffect(() => {
        if (!isAuthenticated) return;
        let interval = setInterval(() => fetchNotifications(), 120000);
        const onVisibility = () => {
            clearInterval(interval);
            if (!document.hidden) {
                fetchNotifications();
                interval = setInterval(() => fetchNotifications(), 120000);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [isAuthenticated, fetchNotifications]);

    const handleLogin = (accessToken, refreshToken, userData) => {
        setAccessToken(accessToken);
        localStorage.setItem('lastUsername', userData.username);
        setUser(userData);
        setIsAuthenticated(true);
        fetchNotifications();
    };

    const handleLogout = async () => {
        await api.logout();
        clearAccessToken();
        setUser(null);
        setIsAuthenticated(false);
        // Only reset local state — do NOT delete notifications from DB.
        // Each user's notifications stay in DB so they see them on next login.
        setNotifications([]);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9f4f0' }}>
                <Lottie animationData={logoAnimation} loop style={{ width: 320, height: 320 }} />
            </div>
        );
    }

    // Pass a getter so children always read the latest in-memory token
    const token = getAccessToken();

    return (
        <>
            <Router>
                <Routes>
                    {/* Public routes — accessible without login */}
                    <Route path="/intro" element={<IntroPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />

                    {/* Auth-gated routes */}
                    {!isAuthenticated ? (
                        <Route path="*" element={<Login onLogin={handleLogin} />} />
                    ) : (
                        <>
                            <Route path="/" element={
                                <MainLayout
                                    token={token} user={user} onLogout={handleLogout}
                                    notifications={notifications}
                                    addNotification={addNotification}
                                    markAllRead={markAllRead}
                                    clearNotifications={clearNotifications}
                                    refreshNotifications={() => fetchNotifications()}
                                    simplified={simplified}
                                />
                            } />
                            <Route path="/settings" element={
                                <SettingsPage
                                    token={token} user={user} onLogout={handleLogout}
                                    addNotification={addNotification}
                                    simplified={simplified}
                                    onSimplifiedChange={(val) => {
                                        setSimplified(val);
                                        localStorage.setItem('storageLabels', val ? 'simplified' : 'technical');
                                    }}
                                />
                            } />
                            <Route path="/admin" element={
                                user?.role === 'admin'
                                    ? <AdminPage token={token} user={user} addNotification={addNotification} />
                                    : <Navigate to="/" replace />
                            } />
                            <Route path="/register" element={<Navigate to="/" replace />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                    )}
                </Routes>
            </Router>
            <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </>
    );
};

export default App;
